import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge, Loader2, Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { ConfirmDialog, EmptyState, Field, Loading, PageHeader, SimpleSelect, btnGhost, btnPrimary, inputCls } from "@/components/app/common";
import { UnisciMarcheDialog } from "@/components/app/UnisciMarcheDialog";

const empty = { nome: "", fornitore_id: "none", note: "", attivo: true };

function MarcaDialog({ open, onOpenChange, item, fornitori, onSaved }) {
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(item ? { nome: item.nome, fornitore_id: item.fornitore_id || "none", note: item.note, attivo: item.attivo } : empty); }, [open, item]);
  const save = async () => {
    if (!f.nome.trim()) return toast.error("Inserisci il nome della marca");
    setSaving(true);
    const body = { ...f, fornitore_id: f.fornitore_id === "none" ? null : f.fornitore_id };
    try {
      if (item) await api.put(`/marche/${item.id}`, body); else await api.post("/marche", body);
      toast.success(item ? "Marca aggiornata" : "Marca creata");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="marca-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display font-medium text-2xl">{item ? "Modifica marca" : "Nuova marca"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Nome marca"><Input data-testid="marca-nome-input" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} className={inputCls} placeholder="Es. Lumière Botanica" /></Field>
          <Field label="Fornitore principale (facoltativo)"><SimpleSelect testId="marca-fornitore-select" value={f.fornitore_id} onChange={(v) => setF({ ...f, fornitore_id: v })} options={[{ value: "none", label: "Nessuno" }, ...fornitori.map((x) => ({ value: x.id, label: x.nome }))]} /></Field>
          <Field label="Note"><Textarea data-testid="marca-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="rounded-xl border-[#EAE3DC]" rows={3} /></Field>
          <label className="flex items-center justify-between rounded-xl border border-[#EAE3DC] px-4 py-3 text-sm">
            <span>Marca attiva</span>
            <Switch data-testid="marca-attivo-switch" checked={f.attivo} onCheckedChange={(v) => setF({ ...f, attivo: v })} />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className={btnPrimary} onClick={save} disabled={saving} data-testid="marca-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Marche() {
  const [items, setItems] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);
  const [unisci, setUnisci] = useState(null);
  const load = useCallback(() => api.get("/marche").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/fornitori").then((r) => setFornitori(r.data)); }, [load]);
  const remove = async () => {
    try { await api.delete(`/marche/${del.id}`); toast.success("Marca eliminata"); load(); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div data-testid="marche-page">
      <PageHeader overline="Catalogo" title="Marche" subtitle="Le marche sono la dimensione principale di analisi: margini, magazzino e report si leggono per marca.">
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="add-marca-button"><Plus className="h-4 w-4 mr-1" />Nuova marca</Button>
      </PageHeader>
      {!items ? <Loading /> : !items.length ? <EmptyState icon={Tag} title="Nessuna marca" text="Crea la prima marca per assegnarla ai prodotti." /> : (
        <div className="card-soft overflow-x-auto" data-testid="marche-table">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{["Marca", "Fornitore principale", "Prodotti", "Note", "Stato", ""].map((h) => <th key={h} className="text-left font-semibold px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {items.map((m, i) => (
                <tr key={m.id} data-testid={`marca-row-${i}`} className={`hover:bg-[#FAF7F5] ${m.attivo ? "" : "opacity-55"}`}>
                  <td className="px-4 py-3 font-medium">{m.nome}</td>
                  <td className="px-4 py-3 text-[#5E4A42]">{m.fornitore_nome || "—"}</td>
                  <td className="px-4 py-3 tabular" data-testid={`marca-nprodotti-${i}`}>{m.n_prodotti}</td>
                  <td className="px-4 py-3 text-xs text-[#756B64] max-w-[280px] truncate">{m.note || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${m.attivo ? "bg-[#8AA090]/15 text-[#4E6656]" : "bg-[#EAE3DC] text-[#756B64]"}`}>{m.attivo ? "Attiva" : "Non attiva"}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-marca-${i}`} onClick={() => setEdit({ open: true, item: m })} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><Pencil className="h-4 w-4" /></button>
                    {items.length > 1 && <button data-testid={`unisci-marca-${i}`} title="Unisci una marca duplicata in questa" onClick={() => setUnisci(m)} className="p-2 rounded-lg hover:bg-[#F4EFEA] text-[#756B64]"><GitMerge className="h-4 w-4" /></button>}
                    <button data-testid={`delete-marca-${i}`} onClick={() => setDel(m)} className="p-2 rounded-lg hover:bg-[#E07A5F]/10 text-[#B4553D]"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <MarcaDialog open={edit.open} item={edit.item} fornitori={fornitori} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <UnisciMarcheDialog keep={unisci} marche={items || []} onClose={() => setUnisci(null)} onDone={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la marca?" text="Si può eliminare solo se nessun prodotto la usa: per un duplicato usa «Unisci»." onConfirm={remove} />
    </div>
  );
}
